"use client";

import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image, { StaticImageData } from "next/image";

import podd from "@/assets/img/update/invest/podd.webp";
import eceo from "@/assets/img/update/invest/e-ceo.webp";
import endorser from "@/assets/img/update/invest/endorsers.webp";
import decen from "@/assets/img/update/invest/decentralized-g.webp";
import { useLanguage } from "@/utils/LanguageContext";
import "@/styles/sections.css";
import GetInvolvedButton from "@/component/common/GetInvolvedButton";

interface FeatureData {
  id: number;
  icon: StaticImageData;
  titleKey: string;
  descriptionKey: string;
}

const investData: FeatureData[] = [
  {
    id: 1,
    icon: podd,
    titleKey: "home_token_role_1_title",
    descriptionKey: "home_token_role_1_body",
  },
  {
    id: 2,
    icon: eceo,
    titleKey: "home_token_role_2_title",
    descriptionKey: "home_token_role_2_body",
  },
  {
    id: 3,
    icon: endorser,
    titleKey: "home_token_role_3_title",
    descriptionKey: "home_token_role_3_body",
  },
  {
    id: 4,
    icon: decen,
    titleKey: "home_token_role_4_title",
    descriptionKey: "home_token_role_4_body",
  },
];

function FeatureModal({
  feature,
  onClose,
}: {
  feature: FeatureData | null;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const lastActiveElementRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!feature) {
      return;
    }

    lastActiveElementRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current) {
        return;
      }

      const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
      lastActiveElementRef.current?.focus();
    };
  }, [feature, onClose]);

  if (!feature || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div className="core-feature-modal-backdrop" onClick={onClose}>
      <div
        id="core-feature-modal"
        ref={dialogRef}
        className="core-feature-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onClick={(event) => event.stopPropagation()}
      >
        <button
          ref={closeButtonRef}
          type="button"
          className="core-feature-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <div className="core-feature-modal-shell">
          <div className="core-feature-modal-visual">
            <div className="core-feature-modal-icon">
              <Image src={feature.icon} alt="" width={160} height={160} />
            </div>
            <div className="core-feature-modal-index">{String(feature.id).padStart(2, "0")}</div>
          </div>

          <div className="core-feature-modal-copy">
            <h3 id={titleId} className="core-feature-modal-title">
              {t(feature.titleKey)}
            </h3>
            <p id={descriptionId} className="core-feature-modal-description">
              {t(feature.descriptionKey)}
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

const InvestArea = () => {
  const [selectedFeature, setSelectedFeature] = useState<FeatureData | null>(null);
  const { t } = useLanguage();

  return (
    <section
      className="pt-130 pb-140 overflow-hidden core-features home-anchor-target"
      id="core-features"
      aria-labelledby="core-features-title"
    >
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-xl-6 col-lg-7">
            <div className="section-title text-center mb-50">
              <h2 className="title style2" id="core-features-title">{t("home_token_role_title")}</h2>
              <p className="sec-text mt-3">{t("home_token_role_intro")}</p>
            </div>
          </div>
        </div>

        <div className="core-features-grid">
          {investData.map((item) => (
            <button
              key={item.id}
              type="button"
              className="core-feature-card"
              onClick={() => setSelectedFeature(item)}
              aria-haspopup="dialog"
              aria-expanded={selectedFeature?.id === item.id}
              aria-controls={selectedFeature?.id === item.id ? "core-feature-modal" : undefined}
            >
              <span className="core-feature-card-index">{String(item.id).padStart(2, "0")}</span>
              <span className="core-feature-card-icon" aria-hidden="true">
                <Image src={item.icon} alt="" width={128} height={128} />
              </span>
              <span className="core-feature-card-title">{t(item.titleKey)}</span>
            </button>
          ))}
        </div>
        <GetInvolvedButton />
      </div>

      <FeatureModal feature={selectedFeature} onClose={() => setSelectedFeature(null)} />
    </section>
  );
};

export default InvestArea;
